// CarerPricingCalculator.jsx
// Supplied verbatim by JCC. Embedded inside the Pro Carer Circle Hub.
import React, { useMemo, useState } from "react";

const DEFAULT_VALUES = {
  desiredMonthlyTakeHome: 2600,
  weeksWorkedPerYear: 46,
  daysWorkedPerWeek: 7,
  annualBusinessCosts: 3500,
  taxAndNIReserve: 25,
  pensionReserve: 5,
  contingencyReserve: 5,
  placementCostsPerDay: 0,
  complexityUplift: 0,
  currentDayRate: "",
};

const currency = (value, decimals = 0) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Number.isFinite(value) ? value : 0);

const numberValue = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const roundUpToNearestFive = (value) => Math.ceil(value / 5) * 5;

function NumberField({
  id,
  label,
  help,
  value,
  onChange,
  prefix,
  suffix,
  min = 0,
  max,
  step = 1,
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="mb-1.5 block text-sm font-semibold text-slate-800"
      >
        {label}
      </label>

      <div className="relative">
        {prefix && (
          <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-slate-500">
            {prefix}
          </span>
        )}

        <input
          id={id}
          type="number"
          inputMode="decimal"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={onChange}
          className={`w-full rounded-xl border border-slate-300 bg-white py-3 text-sm font-medium text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 ${
            prefix ? "pl-8 pr-3" : suffix ? "pl-3 pr-10" : "px-3"
          }`}
        />

        {suffix && (
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-slate-500">
            {suffix}
          </span>
        )}
      </div>

      {help && <p className="mt-1.5 text-xs leading-5 text-slate-500">{help}</p>}
    </div>
  );
}

export default function CarerPricingCalculator() {
  const [values, setValues] = useState(DEFAULT_VALUES);
  const [copied, setCopied] = useState(false);

  const updateValue = (field) => (event) => {
    setValues((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const resetCalculator = () => {
    setValues(DEFAULT_VALUES);
    setCopied(false);
  };

  const results = useMemo(() => {
    const desiredMonthlyTakeHome = clamp(numberValue(values.desiredMonthlyTakeHome), 0, 100000);
    const weeksWorkedPerYear = clamp(numberValue(values.weeksWorkedPerYear), 1, 52);
    const daysWorkedPerWeek = clamp(numberValue(values.daysWorkedPerWeek), 1, 7);
    const annualBusinessCosts = clamp(numberValue(values.annualBusinessCosts), 0, 1000000);
    const taxAndNIReserve = clamp(numberValue(values.taxAndNIReserve), 0, 60);
    const pensionReserve = clamp(numberValue(values.pensionReserve), 0, 30);
    const contingencyReserve = clamp(numberValue(values.contingencyReserve), 0, 30);
    const placementCostsPerDay = clamp(numberValue(values.placementCostsPerDay), 0, 10000);
    const complexityUplift = clamp(numberValue(values.complexityUplift), 0, 100);
    const currentDayRate = clamp(numberValue(values.currentDayRate), 0, 10000);

    const annualDaysWorked = weeksWorkedPerYear * daysWorkedPerWeek;
    const desiredAnnualTakeHome = desiredMonthlyTakeHome * 12;

    const totalReservePercentage = (taxAndNIReserve + pensionReserve + contingencyReserve) / 100;
    const safeReservePercentage = Math.min(totalReservePercentage, 0.8);

    const baselineAnnualRevenueNeeded =
      (desiredAnnualTakeHome + annualBusinessCosts) / (1 - safeReservePercentage);

    const baseDayRate = baselineAnnualRevenueNeeded / Math.max(annualDaysWorked, 1);
    const rateBeforeRounding = baseDayRate * (1 + complexityUplift / 100) + placementCostsPerDay;

    const suggestedDayRate = roundUpToNearestFive(rateBeforeRounding);
    const suggestedWeeklyRate = suggestedDayRate * daysWorkedPerWeek;
    const suggestedMonthlyTurnover = (suggestedDayRate * annualDaysWorked) / 12;
    const suggestedAnnualTurnover = suggestedDayRate * annualDaysWorked;

    const totalAnnualReserves = suggestedAnnualTurnover * safeReservePercentage;
    const estimatedAmountAfterCostsAndReserves =
      suggestedAnnualTurnover - annualBusinessCosts - totalAnnualReserves;

    const currentAnnualTurnover = currentDayRate * annualDaysWorked;
    const currentAmountAfterCostsAndReserves =
      currentAnnualTurnover - annualBusinessCosts - currentAnnualTurnover * safeReservePercentage;

    const rateDifference = suggestedDayRate - currentDayRate;

    return {
      desiredAnnualTakeHome,
      annualDaysWorked,
      totalReservePercentage: safeReservePercentage * 100,
      baseDayRate,
      suggestedDayRate,
      suggestedWeeklyRate,
      suggestedMonthlyTurnover,
      suggestedAnnualTurnover,
      totalAnnualReserves,
      estimatedAmountAfterCostsAndReserves,
      currentDayRate,
      currentAmountAfterCostsAndReserves,
      rateDifference,
    };
  }, [values]);

  const copySummary = async () => {
    const summary = `My JCC Carer Pricing Calculator estimate

Suggested day rate: ${currency(results.suggestedDayRate)}
Suggested weekly income: ${currency(results.suggestedWeeklyRate)}
Estimated monthly turnover: ${currency(results.suggestedMonthlyTurnover)}
Days worked per year: ${results.annualDaysWorked}
Estimated annual business costs: ${currency(numberValue(values.annualBusinessCosts))}
My chosen reserve percentage: ${results.totalReservePercentage.toFixed(0)}%

This is a personal planning estimate, not tax, legal or financial advice.`;

    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2200);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section className="mx-auto max-w-6xl" data-testid="carer-pricing-calculator">
      <div className="overflow-hidden rounded-3xl border border-violet-100 bg-white shadow-sm">
        <div className="border-b border-violet-100 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 px-6 py-8 sm:px-10">
          <p className="mb-2 text-sm font-bold uppercase tracking-[0.18em] text-violet-700">
            Pro Carer Tool
          </p>
          <h2 className="max-w-3xl text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Carer Pricing Calculator
          </h2>
          <p className="mt-3 max-w-3xl text-base leading-7 text-slate-600">
            Work backwards from the income you need, your likely costs and the time you plan to work — then use the
            result as a confident starting point for setting your day rate.
          </p>
        </div>

        <div className="grid gap-8 p-6 sm:p-10 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-bold text-slate-900">1. Your income goal</h3>
              <div className="mt-4 grid gap-5 sm:grid-cols-2">
                <NumberField id="desiredMonthlyTakeHome" label="Monthly income you want available to you" help="The amount you would like after setting aside business costs and your own reserves." value={values.desiredMonthlyTakeHome} onChange={updateValue("desiredMonthlyTakeHome")} prefix="£" min={0} step={50} />
                <NumberField id="weeksWorkedPerYear" label="Weeks you expect to work each year" help="Include planned time off, gaps between placements and holidays." value={values.weeksWorkedPerYear} onChange={updateValue("weeksWorkedPerYear")} suffix="weeks" min={1} max={52} />
                <NumberField id="daysWorkedPerWeek" label="Paid days per week" help="Most live-in placements are priced by the day, often across seven days." value={values.daysWorkedPerWeek} onChange={updateValue("daysWorkedPerWeek")} suffix="days" min={1} max={7} />
                <NumberField id="annualBusinessCosts" label="Annual business costs" help="For example: insurance, DBS, training, accountancy, equipment, marketing and unreimbursed travel." value={values.annualBusinessCosts} onChange={updateValue("annualBusinessCosts")} prefix="£" min={0} step={100} />
              </div>
            </div>

            <div className="border-t border-slate-200 pt-8">
              <h3 className="text-lg font-bold text-slate-900">2. Your reserve allowances</h3>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                These are personal planning choices. They are not tax advice, and they should be reviewed with an
                accountant where needed.
              </p>
              <div className="mt-4 grid gap-5 sm:grid-cols-3">
                <NumberField id="taxAndNIReserve" label="Tax and NI reserve" help="A percentage you personally set aside from turnover." value={values.taxAndNIReserve} onChange={updateValue("taxAndNIReserve")} suffix="%" min={0} max={60} step={1} />
                <NumberField id="pensionReserve" label="Pension or future reserve" help="Optional provision for your longer-term financial security." value={values.pensionReserve} onChange={updateValue("pensionReserve")} suffix="%" min={0} max={30} step={1} />
                <NumberField id="contingencyReserve" label="Contingency reserve" help="For illness, quieter periods, emergencies or unexpected costs." value={values.contingencyReserve} onChange={updateValue("contingencyReserve")} suffix="%" min={0} max={30} step={1} />
              </div>
            </div>

            <div className="border-t border-slate-200 pt-8">
              <h3 className="text-lg font-bold text-slate-900">3. Placement adjustments</h3>
              <div className="mt-4 grid gap-5 sm:grid-cols-2">
                <NumberField id="placementCostsPerDay" label="Placement costs included in your rate" help="Leave as £0 where the family reimburses food, travel or agreed placement expenses separately." value={values.placementCostsPerDay} onChange={updateValue("placementCostsPerDay")} prefix="£" suffix="/ day" min={0} step={5} />
                <NumberField id="complexityUplift" label="Complexity or responsibility uplift" help="Optional uplift for complex care, specialist skills, short notice or greater responsibility." value={values.complexityUplift} onChange={updateValue("complexityUplift")} suffix="%" min={0} max={100} step={5} />
              </div>
            </div>

            <div className="border-t border-slate-200 pt-8">
              <h3 className="text-lg font-bold text-slate-900">4. Compare your current rate</h3>
              <div className="mt-4 max-w-sm">
                <NumberField id="currentDayRate" label="Your current day rate (optional)" help="Enter this only if you would like to compare your current rate with the suggested rate." value={values.currentDayRate} onChange={updateValue("currentDayRate")} prefix="£" suffix="/ day" min={0} step={5} />
              </div>
            </div>

            <div className="flex flex-wrap gap-3 border-t border-slate-200 pt-8">
              <button type="button" onClick={copySummary} className="rounded-xl bg-violet-700 px-5 py-3 text-sm font-bold text-white transition hover:bg-violet-800 focus:outline-none focus:ring-4 focus:ring-violet-200" data-testid="calc-copy">
                {copied ? "Copied to clipboard" : "Copy my estimate"}
              </button>
              <button type="button" onClick={() => window.print()} className="rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-100">
                Print estimate
              </button>
              <button type="button" onClick={resetCalculator} className="rounded-xl px-5 py-3 text-sm font-bold text-slate-600 transition hover:bg-slate-100 focus:outline-none focus:ring-4 focus:ring-slate-100" data-testid="calc-reset">
                Reset example figures
              </button>
            </div>
          </div>

          <aside aria-live="polite" className="h-fit rounded-3xl border border-violet-200 bg-violet-50 p-6 lg:sticky lg:top-6">
            <p className="text-sm font-bold uppercase tracking-[0.16em] text-violet-700">Your starting point</p>
            <p className="mt-3 text-sm leading-6 text-slate-600">Suggested minimum day rate:</p>
            <div className="mt-1 text-5xl font-black tracking-tight text-slate-900" data-testid="calc-day-rate">
              {currency(results.suggestedDayRate)}
              <span className="ml-2 text-lg font-bold text-slate-500">/ day</span>
            </div>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              This is rounded up to the nearest £5 to give you a practical figure for discussion.
            </p>
            <div className="mt-6 space-y-4 border-t border-violet-200 pt-6">
              <div className="flex items-start justify-between gap-5">
                <span className="text-sm text-slate-600">Weekly income</span>
                <strong className="text-right text-sm text-slate-900">{currency(results.suggestedWeeklyRate)}</strong>
              </div>
              <div className="flex items-start justify-between gap-5">
                <span className="text-sm text-slate-600">Estimated monthly turnover</span>
                <strong className="text-right text-sm text-slate-900">{currency(results.suggestedMonthlyTurnover)}</strong>
              </div>
              <div className="flex items-start justify-between gap-5">
                <span className="text-sm text-slate-600">Paid days per year</span>
                <strong className="text-right text-sm text-slate-900">{results.annualDaysWorked}</strong>
              </div>
              <div className="flex items-start justify-between gap-5">
                <span className="text-sm text-slate-600">Your total reserve allowance</span>
                <strong className="text-right text-sm text-slate-900">{results.totalReservePercentage.toFixed(0)}%</strong>
              </div>
              <div className="flex items-start justify-between gap-5">
                <span className="text-sm text-slate-600">Estimated annual reserves</span>
                <strong className="text-right text-sm text-slate-900">{currency(results.totalAnnualReserves)}</strong>
              </div>
            </div>

            {results.currentDayRate > 0 && (
              <div className="mt-6 rounded-2xl border border-white bg-white/80 p-4">
                <p className="text-sm font-bold text-slate-900">Current-rate comparison</p>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  At {currency(results.currentDayRate)}/day, you would have an estimated{" "}
                  <strong className="text-slate-900">{currency(results.currentAmountAfterCostsAndReserves)}</strong>{" "}
                  left annually after the costs and reserve percentages you entered.
                </p>
                <p className="mt-3 text-sm font-bold text-violet-800">
                  {results.rateDifference > 0
                    ? `${currency(results.rateDifference)} more per day may be needed to reach your target.`
                    : results.rateDifference < 0
                    ? `${currency(Math.abs(results.rateDifference))} per day above the suggested minimum.`
                    : "Your current rate matches this suggested minimum."}
                </p>
              </div>
            )}
          </aside>
        </div>

        <div className="border-t border-slate-200 bg-slate-50 px-6 py-7 sm:px-10">
          <div className="grid gap-6 lg:grid-cols-2">
            <div>
              <h3 className="text-base font-bold text-slate-900">How the estimate is calculated</h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                The calculator starts with the annual income you want available to you. It adds your expected business
                costs, allows for the reserve percentages you chose, divides this across your planned paid days, then
                applies any agreed placement costs or complexity uplift.
              </p>
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">Important</h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                This tool is for personal planning only. It does not provide tax, financial, legal, employment-status or
                IR35 advice. Your actual tax and National Insurance position depends on your individual circumstances,
                so seek professional advice where appropriate.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
